import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent } from "react";
import { api, ApiError, jsonOptions } from "./api";
import { DirectionSelect, ErrorBanner, LoadingBlock, Modal } from "./components";
import type { Activity, DayPayload, Direction, IntervalCreateResult, TimeEntry } from "../shared/types";
import { aggregateEntryDetails } from "../shared/entryAggregation";
import { addDays, buildMinuteInterval, formatMinutes, zonedNow } from "../shared/time";

function displayDate(value: string) {
  return new Intl.DateTimeFormat("zh-CN", { month: "long", day: "numeric", weekday: "long", timeZone: "UTC" }).format(new Date(`${value}T00:00:00Z`));
}

const hourLabel = (hour: number) => `${String(hour).padStart(2, "0")}:00`;
const clockLabel = (hour: number, minute: number) => `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
const parseInteger = (value: string) => /^\d+$/.test(value) ? Number(value) : null;

export function totalEntryMinutes(entries: Array<Pick<TimeEntry, "durationMinutes">>) {
  return entries.reduce((sum, entry) => sum + entry.durationMinutes, 0);
}

export function formatHourMinuteTotal(minutes: number) {
  return `${minutes} / 60 分钟`;
}

function defaultEndTime(date: string, today: string, selectedHour: number) {
  if (date !== today) return { hour: selectedHour, minute: 60 };
  const current = zonedNow("Asia/Hong_Kong");
  if (current.minute > 0) return { hour: current.hour, minute: current.minute };
  if (current.hour > 0) return { hour: current.hour - 1, minute: 60 };
  return { hour: 0, minute: 1 };
}

interface EntryDraft { activityId: string; durationMinutes: number; note: string; slotHour: number; }

interface DailyNotes { morningNote: string; forenoonNote: string; afternoonNote: string; eveningSummary: string; }

export function formatNotesForClipboard(date: string, notes: DailyNotes) {
  const [, month, day] = date.split("-");
  return [
    `${month}.${day}`,
    `早上：${notes.morningNote.trim()}`,
    `上午：${notes.forenoonNote.trim()}`,
    `下午：${notes.afternoonNote.trim()}`,
    `晚上：${notes.eveningSummary.trim()}`,
  ].join("\n");
}

async function writeClipboardText(text: string) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    // Fall through for browsers that expose the Clipboard API but block it.
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("CLIPBOARD_WRITE_FAILED");
}

function EntryModal({ date, today, hour, entry, activities, directions, usedMinutes, slotEditCapacity, onClose, onSaved }: {
  date: string; today: string; hour: number; entry?: TimeEntry; activities: Activity[]; directions: Direction[];
  usedMinutes: number[]; slotEditCapacity: number[];
  onClose: () => void; onSaved: (savedEntries: TimeEntry[]) => void | Promise<void>;
}) {
  const active = activities.filter((item) => !item.archivedAt || item.id === entry?.activityId);
  const initialDirection = entry?.directionId ?? active[0]?.directionId ?? directions[0]?.id ?? "";
  const [directionId, setDirectionId] = useState(initialDirection);
  const initialActivities = active.filter((item) => item.directionId === initialDirection);
  const initialAvailable = Math.max(0, slotEditCapacity[hour] - usedMinutes[hour] + (entry?.durationMinutes ?? 0));
  const [draft, setDraft] = useState<EntryDraft>({
    activityId: entry?.activityId ?? initialActivities[0]?.id ?? "__new",
    durationMinutes: entry?.durationMinutes ?? initialAvailable, note: entry?.note ?? "", slotHour: entry?.slotHour ?? hour,
  });
  const initialEnd = defaultEndTime(date, today, hour);
  const [mode, setMode] = useState<"end" | "minutes">(entry ? "minutes" : "end");
  const [endHour, setEndHour] = useState(String(initialEnd.hour));
  const [endMinute, setEndMinute] = useState(String(initialEnd.minute));
  const [newName, setNewName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const children = active.filter((item) => item.directionId === directionId);
  const selectedUsed = usedMinutes[draft.slotHour] ?? 0;
  const usedWithoutEditingEntry = Math.max(0, selectedUsed - (entry?.slotHour === draft.slotHour ? entry.durationMinutes : 0));
  const availableForSelected = Math.max(0, (slotEditCapacity[draft.slotHour] ?? 0) - usedWithoutEditingEntry);
  const minutesValid = Number.isInteger(draft.durationMinutes) && draft.durationMinutes >= 1 && draft.durationMinutes <= availableForSelected;
  const intervalPreview = useMemo(() => {
    if (mode !== "end" || entry) return null;
    const parsedEndHour = parseInteger(endHour);
    const parsedEndMinute = parseInteger(endMinute);
    if (selectedUsed >= 60) return { valid: false as const, message: "开始小时已经填满，请选择其他小时。", parts: [] };
    if (parsedEndHour === null || parsedEndHour < 0 || parsedEndHour > 23) {
      return { valid: false as const, message: "结束小时请输入 0 到 23 的整数。", parts: [] };
    }
    if (parsedEndMinute === null || parsedEndMinute < 1 || parsedEndMinute > 60) {
      return { valid: false as const, message: "结束分钟请输入 1 到 60 的整数。", parts: [] };
    }
    let parts;
    try {
      parts = buildMinuteInterval(date, draft.slotHour, selectedUsed, parsedEndHour, parsedEndMinute);
    } catch (cause) {
      const code = cause instanceof Error ? cause.message : "INVALID_INTERVAL";
      return {
        valid: false as const,
        message: code === "INTERVAL_END_NOT_AFTER_START" ? "结束时间必须晚于开始时间。" : "请检查结束时间。",
        parts: [],
      };
    }
    for (const part of parts) {
      const used = usedMinutes[part.slotHour] ?? 0;
      if (part.slotHour !== draft.slotHour && used > 0) {
        return { valid: false as const, message: `${hourLabel(part.slotHour)} 已有记录，请先处理后再保存。`, parts: [] };
      }
      const capacity = slotEditCapacity[part.slotHour] ?? 0;
      if (capacity === 0) return { valid: false as const, message: "结束时间包含未来小时，不能保存。", parts: [] };
      if (used + part.durationMinutes > capacity) {
        return { valid: false as const, message: `${hourLabel(part.slotHour)} 的可用分钟不足。`, parts: [] };
      }
    }
    const total = parts.reduce((sum, part) => sum + part.durationMinutes, 0);
    return { valid: true as const, message: `将记录 ${formatMinutes(total)}，拆成 ${parts.length} 条小时记录。`, parts };
  }, [date, draft.slotHour, endHour, endMinute, entry, mode, selectedUsed, slotEditCapacity, usedMinutes]);

  function changeDirection(next: string) {
    const nextChildren = active.filter((item) => item.directionId === next);
    setDirectionId(next);
    setDraft((current) => ({ ...current, activityId: nextChildren[0]?.id ?? "__new" }));
    setNewName("");
  }

  async function submit(event: FormEvent) {
    event.preventDefault(); setError("");
    if (mode === "end" && !intervalPreview?.valid) {
      setError(intervalPreview?.message ?? "请检查结束时间。");
      return;
    }
    if (mode === "minutes" && !minutesValid) {
      setError(`该小时最多还能记录 ${availableForSelected} 分钟。`);
      return;
    }
    setSaving(true);
    try {
      let activityId = draft.activityId;
      if (activityId === "__new") {
        if (!newName.trim()) throw new ApiError("VALIDATION", "请输入新事项名称。");
        const created = await api<Activity>("/activities", jsonOptions("POST", { name: newName.trim(), directionId }));
        activityId = created.id;
      }
      if (mode === "end" && !entry) {
        const result = await api<IntervalCreateResult>(`/days/${date}/entries/interval`, jsonOptions("POST", {
          startHour: draft.slotHour,
          expectedStartMinute: selectedUsed,
          endHour: parseInteger(endHour),
          endMinute: parseInteger(endMinute),
          activityId,
          note: draft.note,
        }));
        await onSaved(result.entries);
      } else {
        const path = entry ? `/days/${date}/entries/${entry.id}` : `/days/${date}/entries`;
        const savedEntry = await api<TimeEntry>(path, jsonOptions(entry ? "PATCH" : "POST", { ...draft, activityId }));
        await onSaved([savedEntry]);
      }
    } catch (cause) { setError(cause instanceof ApiError ? cause.message : "保存失败。"); }
    finally { setSaving(false); }
  }

  return <Modal title={entry ? "编辑时间记录" : `${hourLabel(hour)} 添加记录`} onClose={onClose}>
    <form className="modal-form" onSubmit={submit}>
      {error && <ErrorBanner message={error} onDismiss={() => setError("")} />}
      {!entry && <div className="entry-mode-switch" role="group" aria-label="记录时间方式">
        <button type="button" className={mode === "end" ? "active" : ""} aria-pressed={mode === "end"} onClick={() => { setMode("end"); setError(""); }}>结束时间</button>
        <button type="button" className={mode === "minutes" ? "active" : ""} aria-pressed={mode === "minutes"} onClick={() => { setMode("minutes"); setError(""); }}>分钟数</button>
      </div>}
      <label>父方向<DirectionSelect directions={directions} value={directionId} onChange={changeDirection} /></label>
      <label>子事项<select value={draft.activityId} onChange={(event) => setDraft({ ...draft, activityId: event.target.value })}>
        {children.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        <option value="__new">＋ 新建事项</option>
      </select></label>
      {draft.activityId === "__new" && <label>新事项名称<input value={newName} onChange={(event) => setNewName(event.target.value)} maxLength={80} placeholder={`添加到${directions.find((item) => item.id === directionId)?.name ?? "当前方向"}`} autoFocus /></label>}
      {mode === "end" && !entry ? <>
        <div className="form-grid two">
          <label>开始小时<select value={draft.slotHour} onChange={(e) => setDraft({ ...draft, slotHour: Number(e.target.value) })}>
            {Array.from({ length: 24 }, (_, item) => <option key={item} value={item}>{hourLabel(item)}</option>)}
          </select></label>
          <div className="start-time-readout"><span>计算开始时间</span><strong>{clockLabel(draft.slotHour + Math.floor(selectedUsed / 60), selectedUsed % 60)}</strong><small>该小时已有 {selectedUsed} 分钟</small></div>
        </div>
        <div className="form-grid two end-time-grid">
          <label>结束小时<input type="number" inputMode="numeric" min={0} max={23} step={1} value={endHour} onChange={(e) => setEndHour(e.target.value)} /></label>
          <label>结束分钟（1–60）<input type="number" inputMode="numeric" min={1} max={60} step={1} value={endMinute} onChange={(e) => setEndMinute(e.target.value)} /></label>
        </div>
        <div className="form-hint">分钟填 60 表示到该小时结束，例如 10 点 60 分等于 11:00。</div>
        <div className={`interval-feedback ${intervalPreview?.valid ? "success" : "warning"}`} aria-live="polite">{intervalPreview?.message}</div>
      </> : <>
        <div className="form-grid two">
          <label>小时槽<select value={draft.slotHour} onChange={(e) => setDraft({ ...draft, slotHour: Number(e.target.value) })}>
            {Array.from({ length: 24 }, (_, item) => <option key={item} value={item}>{hourLabel(item)}</option>)}
          </select></label>
          <label>分钟数<input type="number" min={1} max={Math.max(1, availableForSelected)} value={draft.durationMinutes} onChange={(e) => setDraft({ ...draft, durationMinutes: Number(e.target.value) })} /></label>
        </div>
        <div className={`form-hint ${minutesValid ? "" : "warning"}`}>当前小时可用：{availableForSelected}分钟</div>
      </>}
      <label>备注（可选）<input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} maxLength={500} placeholder="补充当时的情况" /></label>
      <footer className="modal-actions"><button type="button" className="secondary-button" onClick={onClose}>取消</button><button className="primary-button" disabled={saving || (draft.activityId === "__new" && !newName.trim()) || (mode === "end" ? !intervalPreview?.valid : !minutesValid)}>{saving ? "保存中…" : "保存记录"}</button></footer>
    </form>
  </Modal>;
}

function RangeModal({ date, activities, directions, onClose, onSaved }: { date: string; activities: Activity[]; directions: Direction[]; onClose: () => void; onSaved: () => void | Promise<void> }) {
  const active = activities.filter((item) => !item.archivedAt);
  const initialDirection = active[0]?.directionId ?? directions[0]?.id ?? "";
  const initialChildren = active.filter((item) => item.directionId === initialDirection);
  const [directionId, setDirectionId] = useState(initialDirection);
  const [form, setForm] = useState({ localDate: date, startHour: 0, endHour: 1, activityId: initialChildren[0]?.id ?? "", note: "" });
  const [preview, setPreview] = useState<{ parts: Array<{ localDate: string; slotHour: number; durationMinutes: number }>; conflicts: Array<{ localDate: string; slotHour: number; availableMinutes: number }>; canApply: boolean } | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const children = active.filter((item) => item.directionId === directionId);
  function changeDirection(next: string) {
    const nextChildren = active.filter((item) => item.directionId === next);
    setDirectionId(next);
    setForm((current) => ({ ...current, activityId: nextChildren[0]?.id ?? "" }));
    setPreview(null);
  }
  async function run(apply: boolean) {
    setBusy(true); setError("");
    try {
      const result = await api<typeof preview extends infer _ ? NonNullable<typeof preview> : never>("/entries/range", jsonOptions("POST", { ...form, apply }));
      if (apply) await onSaved(); else setPreview(result);
    } catch (cause) { setError(cause instanceof ApiError ? cause.message : "范围处理失败。"); }
    finally { setBusy(false); }
  }
  return <Modal title="整小时范围填充" onClose={onClose} wide>
    <div className="modal-form">
      {error && <ErrorBanner message={error} onDismiss={() => setError("")} />}
      <div className="form-grid two"><label>开始小时<select value={form.startHour} onChange={(e) => { setForm({ ...form, startHour: Number(e.target.value) }); setPreview(null); }}>{Array.from({ length: 24 }, (_, hour) => <option key={hour} value={hour}>{hourLabel(hour)}</option>)}</select></label><label>结束小时（包含）<select value={form.endHour} onChange={(e) => { setForm({ ...form, endHour: Number(e.target.value) }); setPreview(null); }}>{Array.from({ length: 24 }, (_, hour) => <option key={hour} value={hour}>{hourLabel(hour)}</option>)}</select></label></div>
      <label>父方向<DirectionSelect directions={directions} value={directionId} onChange={changeDirection} /></label>
      <label>子事项<select value={form.activityId} onChange={(e) => { setForm({ ...form, activityId: e.target.value }); setPreview(null); }} disabled={!children.length}>{children.length ? children.map((item) => <option key={item.id} value={item.id}>{item.name}</option>) : <option value="">该方向暂无事项</option>}</select></label>
      {!children.length && <div className="form-hint warning">请先到事项库中为“{directions.find((item) => item.id === directionId)?.name ?? "当前方向"}”创建事项。</div>}
      <label>备注（可选）<input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></label>
      {preview && <div className={`range-preview ${preview.canApply ? "success" : "conflict"}`}>
        <strong>{preview.canApply ? `将创建 ${preview.parts.length} 条整小时记录` : `发现 ${preview.conflicts.length} 处冲突`}</strong>
        <p>{preview.canApply ? `${preview.parts.length * 60} 分钟将填入 ${date}。` : preview.conflicts.map((item) => `${item.slotHour}:00 仅余${item.availableMinutes}分钟`).join("；")}</p>
      </div>}
      <footer className="modal-actions"><button className="secondary-button" onClick={() => run(false)} disabled={busy || !form.activityId || form.endHour < form.startHour}>{busy ? "检查中…" : "预览冲突"}</button><button className="primary-button" onClick={() => run(true)} disabled={busy || !preview?.canApply}>确认填充</button></footer>
    </div>
  </Modal>;
}

export function DayPage({ initialDate, directions }: { initialDate: string; directions: Direction[]; onDirectionsChanged?: () => Promise<void> }) {
  const [date, setDate] = useState(initialDate);
  const [day, setDay] = useState<DayPayload | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [hoveredDirectionId, setHoveredDirectionId] = useState<string | null>(null);
  const [focusedDirectionId, setFocusedDirectionId] = useState<string | null>(null);
  const [pinnedDirectionId, setPinnedDirectionId] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ hour: number; entry?: TimeEntry; returnFocusKey: string } | null>(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [notes, setNotes] = useState({ morningNote: "", forenoonNote: "", afternoonNote: "", eveningSummary: "" });
  const activeDate = useRef(date);
  const loadSequence = useRef(0);
  const restoreFrame = useRef<number | null>(null);
  activeDate.current = date;

  const load = useCallback(async (showLoading: boolean) => {
    if (date !== activeDate.current) return;
    const sequence = ++loadSequence.current;
    if (showLoading) setLoading(true);
    setError("");
    try {
      const [dayResult, activityResult] = await Promise.all([api<DayPayload>(`/days/${date}`), api<Activity[]>("/activities")]);
      if (sequence !== loadSequence.current) return;
      setDay(dayResult); setActivities(activityResult);
      if (showLoading) setNotes({
        morningNote: dayResult.day.morningNote,
        forenoonNote: dayResult.day.forenoonNote,
        afternoonNote: dayResult.day.afternoonNote,
        eveningSummary: dayResult.day.eveningSummary,
      });
    } catch (cause) {
      if (sequence === loadSequence.current) setError(cause instanceof ApiError ? cause.message : "读取失败。");
    } finally {
      if (showLoading && sequence === loadSequence.current) setLoading(false);
    }
  }, [date]);
  useEffect(() => { void load(true); }, [load]);
  useEffect(() => { setHoveredDirectionId(null); setFocusedDirectionId(null); setPinnedDirectionId(null); }, [date]);
  useEffect(() => () => {
    if (restoreFrame.current !== null) window.cancelAnimationFrame(restoreFrame.current);
  }, []);
  const byHour = useMemo(() => Array.from({ length: 24 }, (_, hour) => (day?.entries.filter((entry) => entry.slotHour === hour) ?? []).sort((a, b) => a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt))), [day]);
  const usedMinutesByHour = useMemo(() => byHour.map(totalEntryMinutes), [byHour]);
  const entryDetailsByDirection = useMemo(() => aggregateEntryDetails(day?.entries ?? []), [day]);
  const distribution = useMemo(() => {
    const dayMinutes = 24 * 60;
    const recordedMinutes = day ? Object.values(day.directionTotals).reduce((sum, value) => sum + value, 0) : 0;
    const segments: Array<{ key: string; label: string; minutes: number; color?: string; untracked?: boolean }> = day ? [
      ...directions.filter((direction) => (day.directionTotals[direction.id] ?? 0) > 0).map((direction) => ({ key: direction.id, label: direction.name, minutes: day.directionTotals[direction.id] ?? 0, color: direction.color })),
      ...(recordedMinutes < dayMinutes ? [{ key: "untracked", label: "未记录", minutes: dayMinutes - recordedMinutes, untracked: true }] : []),
    ] : [];
    const total = day ? dayMinutes : 0;
    return {
      total,
      segments: segments.map((segment) => ({ ...segment, percentage: total > 0 ? segment.minutes / total * 100 : 0 })),
      label: total > 0
        ? segments.map((segment) => `${segment.label} ${formatMinutes(segment.minutes)}，${Math.round(segment.minutes / total * 100)}%`).join("；")
        : "暂无可记录时间",
    };
  }, [day, directions]);
  const activeDirectionId = pinnedDirectionId ?? hoveredDirectionId ?? focusedDirectionId;
  const activeSegment = activeDirectionId ? distribution.segments.find((segment) => segment.key === activeDirectionId && !segment.untracked) : undefined;
  const activeDetails = activeDirectionId ? entryDetailsByDirection.get(activeDirectionId) ?? [] : [];

  function takeViewSnapshot(focusKey?: string, fallbackFocusKey?: string) {
    return { x: window.scrollX, y: window.scrollY, focusKey, fallbackFocusKey };
  }

  function restoreView(snapshot: ReturnType<typeof takeViewSnapshot>) {
    if (restoreFrame.current !== null) window.cancelAnimationFrame(restoreFrame.current);
    restoreFrame.current = window.requestAnimationFrame(() => {
      window.scrollTo(snapshot.x, snapshot.y);
      const candidates = [snapshot.focusKey, snapshot.fallbackFocusKey].filter(Boolean);
      const controls = Array.from(document.querySelectorAll<HTMLElement>("[data-focus-key]"));
      const target = candidates.map((key) => controls.find((control) => control.dataset.focusKey === key)).find((control) => control && !(control instanceof HTMLButtonElement && control.disabled));
      target?.focus({ preventScroll: true });
      restoreFrame.current = null;
    });
  }

  async function refreshPreservingView(focusKey?: string, fallbackFocusKey?: string) {
    const snapshot = takeViewSnapshot(focusKey, fallbackFocusKey);
    await load(false);
    restoreView(snapshot);
  }

  function closeEditor() {
    if (!editing) return;
    const snapshot = takeViewSnapshot(editing.returnFocusKey, `hour-add-${editing.hour}`);
    setEditing(null);
    restoreView(snapshot);
  }

  function closeRange() {
    const snapshot = takeViewSnapshot("range-fill");
    setRangeOpen(false);
    restoreView(snapshot);
  }

  async function saveNotes() {
    try { await api(`/days/${date}`, jsonOptions("PUT", notes)); setNotice("每日附记已保存"); window.setTimeout(() => setNotice(""), 2200); }
    catch (cause) { setError(cause instanceof ApiError ? cause.message : "附记保存失败。"); }
  }
  async function copyNotes() {
    setError("");
    try { await writeClipboardText(formatNotesForClipboard(date, notes)); setNotice("附记已复制到剪贴板"); window.setTimeout(() => setNotice(""), 2200); }
    catch { setError("复制失败，请检查浏览器剪贴板权限。"); }
  }
  async function remove(entry: TimeEntry) {
    if (!window.confirm(`删除“${entry.activityName}”这条 ${entry.durationMinutes} 分钟记录？`)) return;
    const snapshot = takeViewSnapshot(`entry-delete-${entry.id}`, `hour-add-${entry.slotHour}`);
    try { await api(`/days/${date}/entries/${entry.id}`, { method: "DELETE" }); await load(false); }
    catch (cause) { setError(cause instanceof ApiError ? cause.message : "删除失败。"); }
    restoreView(snapshot);
  }

  async function reorderHour(hour: number, ordered: TimeEntry[], focusKey?: string) {
    if (!day) return;
    const snapshot = takeViewSnapshot(focusKey);
    const previous = day;
    const orders = new Map(ordered.map((entry, index) => [entry.id, index]));
    const entries = day.entries.map((entry) => orders.has(entry.id) ? { ...entry, sortOrder: orders.get(entry.id)! } : entry)
      .sort((a, b) => a.slotHour - b.slotHour || a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt));
    setDay({ ...day, entries });
    restoreView(snapshot);
    try { await api(`/days/${date}/hours/${hour}/order`, jsonOptions("PUT", { entryIds: ordered.map((entry) => entry.id) })); }
    catch (cause) { setDay(previous); setError(cause instanceof ApiError ? cause.message : "顺序保存失败。"); await load(false); restoreView(snapshot); }
  }

  function moveEntry(hour: number, index: number, direction: -1 | 1, focusKey: string) {
    const entries = [...byHour[hour]];
    const target = index + direction;
    if (target < 0 || target >= entries.length) return;
    [entries[index], entries[target]] = [entries[target], entries[index]];
    void reorderHour(hour, entries, focusKey);
  }

  function dropEntry(event: DragEvent, hour: number, targetIndex: number) {
    event.preventDefault();
    const sourceId = event.dataTransfer.getData("text/plain");
    const entries = [...byHour[hour]];
    const sourceIndex = entries.findIndex((entry) => entry.id === sourceId);
    if (sourceIndex < 0 || sourceIndex === targetIndex) return;
    const [moved] = entries.splice(sourceIndex, 1);
    entries.splice(targetIndex, 0, moved);
    void reorderHour(hour, entries);
  }

  return <div className="page day-page">
    <header className="day-intro"><p className="eyebrow">DAILY FLOW</p><p>按小时回顾，把今天如实分配。</p></header>
    <div className="day-toolbar">
      <div className="day-toolbar-date-group"><h1>{displayDate(date)}</h1><div className="header-actions"><button className="date-step" onClick={() => setDate(addDays(date, -1))}>←</button><input aria-label="选择日期" type="date" value={date} max={initialDate} onChange={(e) => setDate(e.target.value)} /><button className="date-step" disabled={date >= initialDate} onClick={() => setDate(addDays(date, 1))}>→</button><button className="secondary-button" onClick={() => setDate(initialDate)}>今天</button><button className="primary-button" data-focus-key="range-fill" onClick={() => setRangeOpen(true)}>＋ 范围填充</button></div></div>
      {!loading && day && <div className="day-toolbar-status" onMouseLeave={() => setHoveredDirectionId(null)}>
        <div className="day-toolbar-summary"><span>已记录 <strong>{formatMinutes(day.recordedMinutes)}</strong></span><span>完成率 <strong>{Math.round(day.completionRate * 100)}%</strong></span></div>
        <div className="distribution-track" role="group" aria-label={distribution.label}>{distribution.segments.map((segment) => segment.untracked
          ? <span key={segment.key} className="untracked-segment" style={{ width: `${segment.percentage}%` }} title={`${segment.label}：${formatMinutes(segment.minutes)}（${Math.round(segment.percentage)}%）`} />
          : <button type="button" key={segment.key} className="distribution-segment" style={{ width: `${segment.percentage}%`, background: segment.color }} aria-label={`${segment.label}：${formatMinutes(segment.minutes)}，${Math.round(segment.percentage)}%，点击固定明细`} aria-describedby={activeDirectionId === segment.key ? "distribution-detail-tooltip" : undefined} aria-pressed={pinnedDirectionId === segment.key} title="点击固定明细" onMouseEnter={() => setHoveredDirectionId(segment.key)} onFocus={() => setFocusedDirectionId(segment.key)} onBlur={() => setFocusedDirectionId((current) => current === segment.key ? null : current)} onClick={() => { const pinned = pinnedDirectionId === segment.key; setPinnedDirectionId(pinned ? null : segment.key); if (pinned) setHoveredDirectionId(null); }} />
        )}</div>
        {activeSegment && <div className={`distribution-detail-tooltip ${pinnedDirectionId ? "pinned" : ""}`} id="distribution-detail-tooltip" role={pinnedDirectionId ? "dialog" : "tooltip"} aria-label={pinnedDirectionId ? "已固定的每日方向明细" : undefined}>
          <header><strong>{activeSegment.label}</strong><span>{formatMinutes(activeSegment.minutes)} · {Math.round(activeSegment.percentage)}%</span>{pinnedDirectionId && <button type="button" className="trend-detail-close" aria-label="关闭固定明细" onClick={() => { setPinnedDirectionId(null); setHoveredDirectionId(null); setFocusedDirectionId(null); }}>×</button>}</header>
          <div className="distribution-detail-list">{activeDetails.map((detail) => <div className="distribution-detail-row" key={detail.key}>
            <div><strong>{detail.activityName}</strong><span>{formatMinutes(detail.minutes)}</span></div>
            <p title={detail.note || "无备注"}>{detail.note || "无备注"}</p>
          </div>)}</div>
        </div>}
      </div>}
    </div>
    {error && <ErrorBanner message={error} onDismiss={() => setError("")} />}{notice && <div className="success-toast">✓ {notice}</div>}
    {loading || !day ? <LoadingBlock /> : <>
      <section className="day-layout">
        <div className="timeline-card">
          <div className="section-heading"><div><h2>24小时分配</h2><p>单个小时内所有事项合计不超过60分钟</p></div><div className="legend">{directions.map((direction) => <span key={direction.id}><i style={{ background: direction.color }} />{direction.name}</span>)}</div></div>
          <div className="timeline-list">
            {byHour.map((entries, hour) => {
              const used = usedMinutesByHour[hour];
              const editCapacity = day.slotEditCapacity[hour];
              const available = Math.max(0, editCapacity - used);
              const future = editCapacity === 0;
              return <div className={`hour-row ${future ? "future" : ""}`} key={hour}>
                <time>{String(hour).padStart(2, "0")}<small>:00</small></time>
                <div className="hour-content">
                  <div className="hour-progress">
                    <div className="hour-bar">{entries.map((entry) => <span key={entry.id} style={{ width: `${entry.durationMinutes / 60 * 100}%`, background: directions.find((direction) => direction.id === entry.directionId)?.color ?? "#98A2B3" }} />)}{!future && <span className="untracked" style={{ width: `${Math.max(0, editCapacity - used) / 60 * 100}%` }} />}</div>
                    <span className="hour-minute-total" aria-label={`该小时已添加 ${used} 分钟`}>{formatHourMinuteTotal(used)}</span>
                  </div>
                  <div className="entry-list">{entries.map((entry, index) => <div key={entry.id} className="entry-item" onDragOver={(event) => event.preventDefault()} onDrop={(event) => dropEntry(event, hour, index)}>
                    <span className="drag-handle" draggable={entries.length > 1} onDragStart={(event) => { event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/plain", entry.id); }} title="拖拽调整顺序">⋮⋮</span>
                    <button className="entry-chip" data-focus-key={`entry-${entry.id}`} style={{ "--category": directions.find((direction) => direction.id === entry.directionId)?.color ?? "#98A2B3" } as React.CSSProperties} onClick={() => setEditing({ hour, entry, returnFocusKey: `entry-${entry.id}` })} title={entry.note || "点击编辑"}>
                      <span className="entry-chip-main"><b>{entry.activityName}</b><span>{entry.durationMinutes}m</span></span>
                      {entry.note.trim() && <small className="entry-note">{entry.note.trim()}</small>}
                    </button>
                    <button className="entry-order" data-focus-key={`entry-order-up-${entry.id}`} disabled={index === 0} onClick={() => moveEntry(hour, index, -1, `entry-order-up-${entry.id}`)} aria-label={`上移${entry.activityName}`}>↑</button>
                    <button className="entry-order" data-focus-key={`entry-order-down-${entry.id}`} disabled={index === entries.length - 1} onClick={() => moveEntry(hour, index, 1, `entry-order-down-${entry.id}`)} aria-label={`下移${entry.activityName}`}>↓</button>
                    <button className="entry-delete" data-focus-key={`entry-delete-${entry.id}`} onClick={() => void remove(entry)} aria-label={`删除${entry.activityName}`}>×</button>
                  </div>)}{!entries.length && <span className="empty-hour">{future ? "尚未到来" : "这一小时还没有记录"}</span>}</div>
                </div>
                <button className="add-hour" data-focus-key={`hour-add-${hour}`} disabled={available <= 0} onClick={() => setEditing({ hour, returnFocusKey: `hour-add-${hour}` })} aria-label={`${hour}点添加记录`}>＋</button>
              </div>;
            })}
          </div>
        </div>
        <aside className="notes-card">
          <div className="section-heading"><div><h2>每日附记</h2><p>时间之外，也留下当天状态。</p></div></div>
          <label><span>早间状态</span><textarea rows={3} value={notes.morningNote} onChange={(e) => setNotes({ ...notes, morningNote: e.target.value })} placeholder="身体状态、醒来时的感受…" /></label>
          <label><span>上午附记</span><textarea rows={3} value={notes.forenoonNote} onChange={(e) => setNotes({ ...notes, forenoonNote: e.target.value })} placeholder="上午的进展与状态…" /></label>
          <label><span>下午附记</span><textarea rows={3} value={notes.afternoonNote} onChange={(e) => setNotes({ ...notes, afternoonNote: e.target.value })} placeholder="下午的进展与状态…" /></label>
          <label><span>晚间总结</span><textarea rows={3} value={notes.eveningSummary} onChange={(e) => setNotes({ ...notes, eveningSummary: e.target.value })} placeholder="今天进展如何？受到什么影响？" /></label>
          <div className="notes-actions">
            <button type="button" className="primary-button" onClick={saveNotes}>保存附记</button>
            <button type="button" className="secondary-button" onClick={copyNotes}>复制附记</button>
          </div>
        </aside>
      </section>
    </>}
    {editing && day && <EntryModal date={date} today={initialDate} hour={editing.hour} entry={editing.entry} activities={activities} directions={directions} usedMinutes={usedMinutesByHour} slotEditCapacity={day.slotEditCapacity} onClose={closeEditor} onSaved={async (savedEntries) => { const first = savedEntries[0]; setEditing(null); await refreshPreservingView(first ? `entry-${first.id}` : undefined, `hour-add-${editing.hour}`); }} />}
    {rangeOpen && <RangeModal date={date} activities={activities} directions={directions} onClose={closeRange} onSaved={async () => { setRangeOpen(false); await refreshPreservingView("range-fill"); }} />}
  </div>;
}
